import { useTranslation } from 'react-i18next';
import { AiDocumentationFieldEnum, Documentation } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import useDocumentFieldDetection from 'routes/Documentation/DocumentView/utils/useDocumentFieldDetection';
import styles from './DocumentCodeCell.module.scss';

type Props = {
  item: Documentation;
  isAiDetectionEnabled?: boolean;
  credits?: AiCredits | null;
};

// Reuses the same AiFieldAssist popover the create/edit form and the
// DocumentView sidebar use, so a document flagged by import detection shows
// the identical Detecting/Failed/MultipleCodes affordance in the list's Code
// column without opening the document first.
export default function DocumentCodeCell({
  item,
  isAiDetectionEnabled,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { code } = useDocumentFieldDetection({
    doc: item,
    documentationVersionId: item.currentVersion?.id,
    isAiEnabled: !!isAiDetectionEnabled,
  });

  if (!isAiDetectionEnabled || code.uiState === AiFieldUiState.None) {
    return <Typography variant="body">{item.code || '-'}</Typography>;
  }

  const showValueBesideAssist =
    !!item.code &&
    (code.uiState === AiFieldUiState.MultipleCodes ||
      code.uiState === AiFieldUiState.Failed);

  return (
    <span className={styles.valueWithAssist}>
      {showValueBesideAssist && (
        <Typography variant="body">{item.code}</Typography>
      )}
      <AiFieldAssist
        field={AiDocumentationFieldEnum.Code}
        resolveSource={code.resolveSource}
        onApplyValue={code.onApplyValue}
        detectionUiState={code.uiState}
        suggestedCodes={code.suggestedCodes}
        detectingLabel={t('ai-detecting-code')}
        onDismiss={code.onDismiss}
        showManualCodeInput
        isSavingManualCode={code.isSaving}
        creditsRemaining={credits}
        labels={{
          idleLabel: t('ai-detect-with-ai'),
          idleCta: t('ai-detect-code-cta'),
          regenerateCta: t('ai-detect-code-again'),
        }}
      />
    </span>
  );
}
